import React, { useEffect, useRef, useState } from 'react';

import { clampToCellBounds, clampSizeToCellBounds } from '../geometry';
import defaultLogo from '../../../images/icononly_transparent_nobuffer.png';
import defaultSlogan from '../../../images/textonly_transparent_nobuffer.png';
import './ImageBlockLayer.css';

// Fixed set of built-in images a block's resourceKey can reference — no
// upload pipeline yet (see blocks/image-block.md Notes). Add entries here
// (and a matching import above) as more built-in images become available.
export const IMAGE_RESOURCES = {
  DEFAULT_LOGO: 'default_logo',
  DEFAULT_SLOGAN: 'default_slogan',
};

const IMAGE_RESOURCE_MAP = {
  [IMAGE_RESOURCES.DEFAULT_LOGO]: defaultLogo,
  [IMAGE_RESOURCES.DEFAULT_SLOGAN]: defaultSlogan,
};

const getImageResource = (resourceKey) => IMAGE_RESOURCE_MAP[resourceKey] || defaultLogo;

// Same inches-as-%-of-usable-area convention as TextBlockLayer (see its
// header comment) — no fixed px-per-inch constant, so this stays correct
// across the responsive on-screen canvas and the true-physical-size
// @media print page alike.
export default function ImageBlockLayer({
  blocks,
  setBlocks,
  editable,
  selectedIds,
  selectionCount,
  onSelect,
  onGroupDragCandidate,
  showBoundaries,
  usableWidthIn,
  usableHeightIn,
}) {
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, blockX: 0, blockY: 0 });
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, blockWidth: 0, blockHeight: 0 });

  const startDrag = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    // Ctrl/Cmd+click and Shift+click are selection-only gestures (toggle /
    // extend) — they never start a drag.
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onSelect(id, e);
      return;
    }
    // A plain mousedown on a block that's already part of a multi-selection
    // is ambiguous until the mouse actually moves (click collapses the
    // selection to just this block; click+drag moves the whole selection)
    // — deferred entirely to PrintableMasterGrid's group-drag candidate
    // machinery.
    if (selectionCount > 1 && selectedIds.includes(id)) {
      onGroupDragCandidate(id, e);
      return;
    }
    onSelect(id, e);
    setActiveId(id);
    const block = blocks.find((b) => b.id === id);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, blockX: block.x, blockY: block.y };
  };

  const startResize = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    onSelect(id, e);
    setResizingId(id);
    const block = blocks.find((b) => b.id === id);
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, blockWidth: block.width, blockHeight: block.height };
  };

  useEffect(() => {
    if (!editable || (activeId === null && resizingId === null)) return undefined;

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;

      if (activeId !== null) {
        const dxIn = ((e.clientX - dragStart.current.mouseX) / rect.width) * usableWidthIn;
        const dyIn = ((e.clientY - dragStart.current.mouseY) / rect.height) * usableHeightIn;
        setBlocks((prev) =>
          prev.map((b) => {
            if (b.id !== activeId) return b;
            // Clamped every move, not just on drop — same rationale as
            // TextBlockLayer's drag handler.
            const { x, y } = clampToCellBounds(
              dragStart.current.blockX + dxIn,
              dragStart.current.blockY + dyIn,
              b.width,
              b.height,
              usableWidthIn,
              usableHeightIn
            );
            return { ...b, x, y };
          })
        );
      }

      if (resizingId !== null) {
        const dwIn = ((e.clientX - resizeStart.current.mouseX) / rect.width) * usableWidthIn;
        const dhIn = ((e.clientY - resizeStart.current.mouseY) / rect.height) * usableHeightIn;
        setBlocks((prev) =>
          prev.map((b) => {
            if (b.id !== resizingId) return b;
            const { width, height } = clampSizeToCellBounds(
              b.x,
              b.y,
              resizeStart.current.blockWidth + dwIn,
              resizeStart.current.blockHeight + dhIn,
              usableWidthIn,
              usableHeightIn
            );
            return { ...b, width, height };
          })
        );
      }
    };
    const handleUp = () => {
      setActiveId(null);
      setResizingId(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [activeId, resizingId, editable, setBlocks, usableWidthIn, usableHeightIn]);

  return (
    <div ref={containerRef} className="ss-print__image-layer">
      {blocks.map((b) => {
        // Defensive clamp at render time too — not just during
        // drag/resize — same rationale as TextBlockLayer: a stale stored
        // position/size (an old save, or a cell that shrank after
        // rows/padding changed) can never render outside the cell border.
        const { x: renderX, y: renderY } = clampToCellBounds(b.x, b.y, b.width, b.height, usableWidthIn, usableHeightIn);
        const { width: renderWidth, height: renderHeight } = clampSizeToCellBounds(
          renderX,
          renderY,
          b.width,
          b.height,
          usableWidthIn,
          usableHeightIn
        );
        const leftPct = usableWidthIn > 0 ? (renderX / usableWidthIn) * 100 : 0;
        const topPct = usableHeightIn > 0 ? (renderY / usableHeightIn) * 100 : 0;
        const widthPct = usableWidthIn > 0 ? (renderWidth / usableWidthIn) * 100 : 0;
        const heightPct = usableHeightIn > 0 ? (renderHeight / usableHeightIn) * 100 : 0;

        return (
          <div
            key={b.id}
            className={[
              'ss-print__image-block',
              editable ? 'is-editable' : '',
              selectedIds.includes(b.id) ? 'is-selected' : '',
              showBoundaries ? 'show-boundaries' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: `${leftPct}%`,
              top: `${topPct}%`,
              width: `${widthPct}%`,
              height: `${heightPct}%`,
              backgroundImage: `url(${getImageResource(b.resourceKey)})`,
            }}
            onMouseDown={(e) => startDrag(e, b.id)}
          >
            {editable && <div className="ss-print__image-resize-handle" onMouseDown={(e) => startResize(e, b.id)} />}
          </div>
        );
      })}
    </div>
  );
}
